/**
 * Push Notifications API
 * Push notification testing and management endpoints
 * NOTE: Matches backend at /v1/admin/push/*
 */

import apiClient from './client';
import type {
  SendTestNotificationRequest,
  SendTestNotificationResponse,
  PushTokensResponse,
  PushLogsResponse,
  PushStats,
  PushUsersResponse,
  PushContentAssistRequest,
  PushContentAssistResponse,
  PushContentTemplateResponse,
} from '@/types/push';

export const pushApi = {
  /**
   * Send a test push notification
   */
  sendTest: async (data: SendTestNotificationRequest): Promise<SendTestNotificationResponse> => {
    const response = await apiClient.post('/push/send-test', data);
    return response.data;
  },

  /**
   * Get push tokens
   * @param params.platform - Filter by platform ('ios' or 'android')
   * @param params.active_only - Only return active tokens (default: true)
   * @param params.page - Page number (1-based, default: 1)
   * @param params.limit - Items per page (default: 50, max: 100)
   */
  getTokens: async (params?: {
    platform?: string;
    active_only?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PushTokensResponse> => {
    const response = await apiClient.get('/push/tokens', { params });
    return response.data;
  },

  /**
   * Get push notification logs
   * @param params.status_filter - Filter by status ('sent' or 'failed')
   * @param params.page - Page number (1-based, default: 1)
   * @param params.limit - Items per page (default: 20, max: 50)
   * @param params.grouped - Group logs by batch (default: true)
   */
  getLogs: async (params?: {
    status_filter?: string;
    page?: number;
    limit?: number;
    grouped?: boolean;
  }): Promise<PushLogsResponse> => {
    const response = await apiClient.get('/push/logs', { params });
    return response.data;
  },

  /**
   * Get push notification statistics
   */
  getStats: async (): Promise<PushStats> => {
    const response = await apiClient.get('/push/stats');
    return response.data;
  },

  /**
   * Get users for push notification targeting
   * @param params.search - Search by name or user_id
   * @param params.has_token - Only return users with active push tokens (default: true)
   * @param params.page - Page number (1-based, default: 1)
   * @param params.limit - Items per page (default: 50, max: 100)
   */
  getUsers: async (params?: {
    search?: string;
    has_token?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PushUsersResponse> => {
    const response = await apiClient.get('/push/users', { params });
    return response.data;
  },

  /**
   * Generate push notification content using AI agent
   * @param data.user_input - User's description of what the notification should convey
   * @param data.agent_type - Optional agent type (default: dora)
   * @param data.language - Output language (default: zh)
   * @param data.tone - Tone of the notification (e.g., 温暖/活泼/正式)
   */
  contentAssist: async (data: PushContentAssistRequest): Promise<PushContentAssistResponse> => {
    const response = await apiClient.post('/push/content-assist', data);
    return response.data;
  },

  /**
   * Get system prompt template for LLM mode (for transparency)
   */
  getContentAssistTemplate: async (): Promise<PushContentTemplateResponse> => {
    const response = await apiClient.get('/push/content-assist/template');
    return response.data;
  },
};
